import type {
  PiniaPersistFailedEvent,
  PiniaPersistStorage,
  PiniaStorageUpdateEvent,
} from '#ui-shared/pinia/persist/persisted-store.types';

/** Prefix every persisted store entry shares, so they can be found and cleared as a group. */
export const PERSISTED_STORE_KEY_PREFIX = 'store.persist.';

/**
 * Backs Pinia persistence with the webview's own `localStorage`.
 *
 * Synchronous `getItem` is the point: it lets a store restore at creation, which state travelling
 * to the native layer and back can never do in time. Writes emit `storage-update`, which the
 * webview store forwards to the native layer as a marker.
 */
export class LocalStoragePersistStorage extends EventTarget implements PiniaPersistStorage {
  /** Keys whose last write failed, so a store failing every tick reports it only once. */
  private readonly failedKeys = new Set<string>();

  getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    }
    catch (error) {
      console.warn(`[persisted-store] Could not read ${key}`, error);

      return null;
    }
  }

  /**
   * Persists a store, emitting `storage-update` only once the value is actually stored.
   * @param key - the storage key to write
   * @param value - the serialized store state
   */
  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    }
    catch (error) {
      // Quota is the expected failure: a map snapshot holds every draw layer's geometry, so a busy
      // operation can outgrow the ~5MB budget.
      this.reportFailure(key, error);

      return;
    }

    this.failedKeys.delete(key);

    // Key only: the listener sends a marker, so the value would just be copied and discarded.
    const event: PiniaStorageUpdateEvent = new CustomEvent('storage-update', {
      detail: { key },
    });

    this.dispatchEvent(event);
  }

  /**
   * Announces that this key is no longer being kept.
   *
   * Skipping the write silently is not enough: the marker from the last write that did land is
   * still with the native layer, so the next persisted bootstrap would restore that older state.
   * @param key - the storage key that could not be written
   * @param error - the failure from the last attempt
   */
  private reportFailure(key: string, error: unknown): void {
    if (this.failedKeys.has(key)) {
      return;
    }

    this.failedKeys.add(key);
    console.warn(`[persisted-store] Could not persist ${key}, skipping`, error);

    const event: PiniaPersistFailedEvent = new CustomEvent('persist-failed', {
      detail: { key },
    });

    this.dispatchEvent(event);
  }
}

/** Shared instance: the Pinia plugin writes to it, the webview store listens to it. */
export const persistedStoreStorage = new LocalStoragePersistStorage();

/** Returns the storage keys of every persisted store currently held. */
export function getPersistedStoreKeys(): string[] {
  try {
    return Object.keys(localStorage).filter(key => key.startsWith(PERSISTED_STORE_KEY_PREFIX));
  }
  catch (error) {
    console.warn('[persisted-store] Could not enumerate persisted stores', error);

    return [];
  }
}

/** Drops every persisted store. Used when a clean bootstrap supersedes the local state. */
export function clearPersistedStores(): void {
  for (const key of getPersistedStoreKeys()) {
    localStorage.removeItem(key);
  }
}
